import { useCallback } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { humanizeDataValue } from '../utils/dataLabels';

/**
 * Lead source values to dictionary keys, covering the full domain of the
 * backend DTO enum (`backend-nest/src/leads/dto/create-lead.dto.ts`) rather
 * than the subset that happens to appear in the UI today.
 *
 * That enum is the widest closed list that exists, but it is NOT the whole
 * story: `Lead.source` is a plain `String` column (`schema.prisma:310`) and the
 * `@IsEnum` gate is bypassed on the external lead-source-connection path,
 * where `payload-mapper.service.ts` casts a customer-supplied string straight
 * through. So this map is exhaustive over the enum and still needs a fallback.
 */
const SOURCE_LABEL_KEYS: Record<string, string> = {
  email: 'common.sources.email',
  linkedin: 'common.sources.linkedin',
  instagram: 'common.sources.instagram',
  facebook: 'common.sources.facebook',
  twitter: 'common.sources.twitter',
  web_form: 'common.sources.webForm',
  scraping: 'common.sources.scraping',
  linkedin_enrichment: 'common.sources.linkedinEnrichment',
  linkedin_segment_search: 'common.sources.linkedinSegmentSearch',
  instagram_enrichment: 'common.sources.instagramEnrichment',
  instagram_segment_search: 'common.sources.instagramSegmentSearch',
  manual: 'common.sources.manual',
  website: 'common.sources.website',
  referral: 'common.sources.referral',
  import: 'common.sources.import',
  other: 'common.sources.other',
  event: 'common.sources.event',
};

/**
 * The translated label for a lead source, replacing three duplicated copies of
 * `formatSourceLabel` that title-cased the raw column value — which is how
 * `linkedin` rendered as "Linkedin" and why the label was English everywhere.
 */
export function useSourceLabel() {
  const { t } = useTranslation();

  return useCallback(
    (source: string): string => {
      const normalized = source.trim().toLowerCase();
      // A blank source is a source we cannot name, which is what "Other" means.
      if (!normalized) {
        return t('common.sources.other');
      }

      const key = SOURCE_LABEL_KEYS[normalized];
      return key ? t(key) : humanizeDataValue(normalized);
    },
    [t],
  );
}
